interface PostMessage<I> {
  message: I;
  options?: StructuredSerializeOptions;
}

interface Task<I, R> {
  postMessage: PostMessage<I>;
  resolve: (ret: R) => void;
}

const maxConcurrency = navigator?.hardwareConcurrency ?? 4;

export const createWorkerPool = <Input, Output, Return>(
  construct: new () => Worker,
  mapResponse: (output: Output | undefined) => Return
) => {
  let numWorkers = 0;
  const taskBacklog: Task<Input, Return>[] = [];

  const sendNext = async () => {
    if (numWorkers >= maxConcurrency) {
      return;
    }
    if (taskBacklog.length === 0) {
      return;
    }

    const task = taskBacklog.shift();
    task?.resolve(await send(task.postMessage));
  };

  const send = (postMessage: PostMessage<Input>): Promise<Return> => {
    const worker = new construct();
    numWorkers++;

    return new Promise((resolve) => {
      worker.onmessage = (e) => {
        worker.terminate();
        numWorkers--;
        resolve(mapResponse(e.data));
        sendNext();
      };
      const onError = () => {
        worker.terminate();
        numWorkers--;
        resolve(mapResponse(undefined));
        sendNext();
      };
      worker.onerror = onError;
      worker.onmessageerror = onError;
      worker.postMessage(postMessage.message, postMessage?.options);
    });
  };

  return (postMessage: PostMessage<Input>): Promise<Return> =>
    new Promise((resolve) => {
      if (numWorkers >= maxConcurrency) {
        taskBacklog.push({
          postMessage,
          resolve
        });
        return;
      }

      (async () => {
        resolve(await send(postMessage));
      })();
    });
};
